import React, { useMemo } from 'react';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Doughnut } from 'react-chartjs-2';

const useStyles = makeStyles(theme => ({
	chartContainer: {
		padding: theme.spacing(3),
		backgroundColor: theme.palette.background.paper,
		borderRadius: 16,
		height: '100%',
		boxShadow: theme.palette.type === 'dark'
			? '0 4px 20px rgba(0, 0, 0, 0.3)'
			: '0 4px 20px rgba(0, 0, 0, 0.08)'
	},
	chartTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(2),
		color: theme.palette.text.primary,
		fontSize: '1.1rem'
	},
	chartWrapper: {
		position: 'relative',
		height: 250,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center'
	},
	legendContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'center',
		gap: theme.spacing(2),
		marginTop: theme.spacing(2)
	},
	legendItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5)
	},
	legendColor: {
		width: 12,
		height: 12,
		borderRadius: '50%'
	},
	legendLabel: {
		fontSize: 12,
		color: theme.palette.text.secondary
	},
	legendValue: {
		fontSize: 12,
		fontWeight: 600,
		color: theme.palette.text.primary,
		marginLeft: 4
	},
	noDataText: {
		color: theme.palette.text.secondary,
		textAlign: 'center'
	}
}));

function EmailCampaignChart({ emailData = [], isCampaignMode = false }) {
	const classes = useStyles();
	const theme = useTheme();

	// Calculate status counts for push mode (email status)
	// Updated to use: success, delivered, queue, failed, received
	const emailStatusCounts = useMemo(() => {
		const counts = {
			success: 0,
			delivered: 0,
			queue: 0,
			failed: 0,
			received: 0
		};

		emailData.forEach(email => {
			const status = (email.status || '').toLowerCase();
			if (counts.hasOwnProperty(status)) {
				counts[status]++;
			}
		});

		return counts;
	}, [emailData]);

	// Calculate campaign type counts for campaign mode
	const campaignTypeCounts = useMemo(() => {
		const counts = {
			instant: 0,
			schedule: 0
		};

		emailData.forEach(campaign => {
			const type = (campaign.campaign_type || '').toLowerCase();
			if (counts.hasOwnProperty(type)) {
				counts[type]++;
			}
		});

		return counts;
	}, [emailData]);

	// Chart colors for email status - Updated
	const emailStatusColors = {
		success: '#22C55E',
		delivered: '#10B981',
		queue: '#F59E0B',
		failed: '#EF4444',
		received: '#8B5CF6'
	};

	// Chart colors for campaign types
	const campaignTypeColors = {
		instant: '#3B82F6',
		schedule: '#F59E0B'
	};

	// Chart data for email status (push mode) - Updated
	const emailStatusChartData = useMemo(() => ({
		labels: ['Success', 'Delivered', 'Queue', 'Failed', 'Received'],
		datasets: [
			{
				data: [
					emailStatusCounts.success,
					emailStatusCounts.delivered,
					emailStatusCounts.queue,
					emailStatusCounts.failed,
					emailStatusCounts.received
				],
				backgroundColor: [
					emailStatusColors.success,
					emailStatusColors.delivered,
					emailStatusColors.queue,
					emailStatusColors.failed,
					emailStatusColors.received
				],
				borderColor: theme.palette.background.paper,
				borderWidth: 3,
				hoverBorderWidth: 0,
				hoverBackgroundColor: [
					emailStatusColors.success,
					emailStatusColors.delivered,
					emailStatusColors.queue,
					emailStatusColors.failed,
					emailStatusColors.received
				]
			}
		]
	}), [emailStatusCounts, theme.palette.background.paper]);

	// Chart data for campaign types (campaign mode)
	const campaignTypeChartData = useMemo(() => ({
		labels: ['Instant', 'Scheduled'],
		datasets: [
			{
				data: [
					campaignTypeCounts.instant,
					campaignTypeCounts.schedule
				],
				backgroundColor: [
					campaignTypeColors.instant,
					campaignTypeColors.schedule
				],
				borderColor: theme.palette.background.paper,
				borderWidth: 3,
				hoverBorderWidth: 0,
				hoverBackgroundColor: [
					campaignTypeColors.instant,
					campaignTypeColors.schedule
				]
			}
		]
	}), [campaignTypeCounts, theme.palette.background.paper]);

	// Chart options
	const chartOptions = useMemo(() => ({
		responsive: true,
		maintainAspectRatio: false,
		cutoutPercentage: 65,
		legend: {
			display: false
		},
		tooltips: {
			enabled: true,
			mode: 'single',
			intersect: true,
			position: 'average',
			backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#1F2937',
			titleFontColor: '#FFFFFF',
			titleFontSize: 13,
			titleFontStyle: 'bold',
			bodyFontColor: '#FFFFFF',
			bodyFontSize: 12,
			xPadding: 12,
			yPadding: 12,
			cornerRadius: 8,
			displayColors: true,
			boxWidth: 10,
			boxHeight: 10,
			caretSize: 6,
			caretPadding: 8,
			callbacks: {
				label: function (tooltipItem, data) {
					const dataset = data.datasets[tooltipItem.datasetIndex];
					const total = dataset.data.reduce((a, b) => a + b, 0);
					const value = dataset.data[tooltipItem.index];
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
					const label = data.labels[tooltipItem.index];
					return ` ${label}: ${value} (${percentage}%)`;
				}
			}
		},
		hover: {
			mode: 'single',
			intersect: true,
			animationDuration: 0
		},
		animation: {
			animateRotate: true,
			animateScale: false,
			duration: 500
		},
		elements: {
			arc: {
				borderWidth: 0
			}
		}
	}), [theme.palette.type]);

	// Check if there's any data
	const hasEmailData = Object.values(emailStatusCounts).some(count => count > 0);
	const hasCampaignData = Object.values(campaignTypeCounts).some(count => count > 0);
	const hasData = isCampaignMode ? hasCampaignData : hasEmailData;

	// Legend items for email status - Updated
	const emailStatusLegendItems = [
		{ label: 'Success', color: emailStatusColors.success, value: emailStatusCounts.success },
		{ label: 'Delivered', color: emailStatusColors.delivered, value: emailStatusCounts.delivered },
		{ label: 'Queue', color: emailStatusColors.queue, value: emailStatusCounts.queue },
		{ label: 'Failed', color: emailStatusColors.failed, value: emailStatusCounts.failed },
		{ label: 'Received', color: emailStatusColors.received, value: emailStatusCounts.received }
	];

	// Legend items for campaign types
	const campaignTypeLegendItems = [
		{ label: 'Instant', color: campaignTypeColors.instant, value: campaignTypeCounts.instant },
		{ label: 'Scheduled', color: campaignTypeColors.schedule, value: campaignTypeCounts.schedule }
	];

	const legendItems = isCampaignMode ? campaignTypeLegendItems : emailStatusLegendItems;
	const chartData = isCampaignMode ? campaignTypeChartData : emailStatusChartData;
	const chartTitle = isCampaignMode ? 'Campaign Distribution' : 'Email Status Distribution';

	return (
		<Paper className={classes.chartContainer}>
			<Typography variant="h6" className={classes.chartTitle}>
				{chartTitle}
			</Typography>

			<div className={classes.chartWrapper}>
				{hasData ? (
					<Doughnut
						data={chartData}
						options={chartOptions}
						redraw={false}
					/>
				) : (
					<Typography className={classes.noDataText}>
						No data available
					</Typography>
				)}
			</div>

			{/* Custom Legend */}
			<div className={classes.legendContainer}>
				{legendItems.map((item) => (
					<div key={item.label} className={classes.legendItem}>
						<div
							className={classes.legendColor}
							style={{ backgroundColor: item.color }}
						/>
						<span className={classes.legendLabel}>{item.label}</span>
						<span className={classes.legendValue}>({item.value})</span>
					</div>
				))}
			</div>
		</Paper>
	);
}

export default EmailCampaignChart;